import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const FIRECalculator = () => {
  const [currentAge, setCurrentAge] = useState(30);
  const [monthlyExpense, setMonthlyExpense] = useState(50000);
  const [currentSavings, setCurrentSavings] = useState(1000000);
  const [monthlySavings, setMonthlySavings] = useState(50000);
  const [preRetireReturn, setPreRetireReturn] = useState(12);
  const [postRetireReturn, setPostRetireReturn] = useState(7);
  const [inflation, setInflation] = useState(6);
  const [withdrawalRate, setWithdrawalRate] = useState(4); // Safe withdrawal rate
  const [result, setResult] = useState(null);

  const calculate = () => {
    // FIRE Number = Annual Expenses / Safe Withdrawal Rate
    const annualExpense = monthlyExpense * 12;
    const fireNumber = annualExpense / (withdrawalRate / 100);
    
    // Calculate years to FIRE
    const monthlyReturn = preRetireReturn / 12 / 100;
    let corpus = currentSavings;
    let yearsToFIRE = 0;
    let yearlyData = [];
    
    const maxYears = 50;
    for (let year = 1; year <= maxYears; year++) {
      // Add monthly savings and returns for the year
      for (let month = 1; month <= 12; month++) {
        corpus = corpus * (1 + monthlyReturn) + monthlySavings;
      }
      
      // Adjust FIRE number for inflation
      const inflationAdjustedFIRE = fireNumber * Math.pow(1 + inflation / 100, year);
      
      yearlyData.push({
        year,
        age: currentAge + year,
        corpus,
        fireNumber: inflationAdjustedFIRE,
        canRetire: corpus >= inflationAdjustedFIRE,
      });
      
      if (corpus >= inflationAdjustedFIRE && yearsToFIRE === 0) {
        yearsToFIRE = year;
      }
    }
    
    const fireAge = currentAge + yearsToFIRE;
    // finalCorpus is the corpus value at end of projection
    // eslint-disable-next-line no-unused-vars
    const finalCorpus = corpus;
    
    // Calculate corpus at FIRE
    const corpusAtFIRE = yearsToFIRE > 0 ? yearlyData[yearsToFIRE - 1].corpus : currentSavings;
    const fireNumberAtRetirement = yearsToFIRE > 0 ? yearlyData[yearsToFIRE - 1].fireNumber : fireNumber;
    
    // Monthly income at FIRE
    const monthlyIncomeAtFIRE = (corpusAtFIRE * withdrawalRate / 100) / 12;
    
    // Savings rate
    const monthlyIncome = monthlyExpense + monthlySavings;
    const savingsRate = (monthlySavings / monthlyIncome) * 100;

    setResult({
      fireNumber,
      fireNumberAtRetirement,
      yearsToFIRE,
      fireAge,
      corpusAtFIRE,
      monthlyIncomeAtFIRE,
      savingsRate,
      totalInvestment: currentSavings + (monthlySavings * 12 * yearsToFIRE),
      wealthGained: corpusAtFIRE - (currentSavings + (monthlySavings * 12 * yearsToFIRE)),
      yearlyData: yearlyData.slice(0, Math.min(yearsToFIRE + 10, maxYears)),
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('FIRE Calculator Report', 105, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Financial Independence, Retire Early', 105, 28, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 36, { align: 'center' });

    autoTable(doc, {
      startY: 45,
      head: [['Parameter', 'Value']],
      body: [
        ['Current Age', `${currentAge} years`],
        ['Monthly Expenses', formatCurrencyForPDF(monthlyExpense)],
        ['Monthly Savings', formatCurrencyForPDF(monthlySavings)],
        ['Savings Rate', `${result.savingsRate.toFixed(1)}%`],
        ['Current Corpus', formatCurrencyForPDF(currentSavings)],
        ['FIRE Number (Today)', formatCurrencyForPDF(result.fireNumber)],
        ['Years to FIRE', `${result.yearsToFIRE} years`],
        ['FIRE Age', `${result.fireAge} years`],
        ['Corpus at FIRE', formatCurrencyForPDF(result.corpusAtFIRE)],
        ['Monthly Income at FIRE', formatCurrencyForPDF(result.monthlyIncomeAtFIRE)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('FIRE_Plan.pdf');
  };

  const chartData = result ? {
    labels: result.yearlyData.map(d => `Age ${d.age}`),
    datasets: [
      {
        label: 'Your Corpus',
        data: result.yearlyData.map(d => d.corpus),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'FIRE Number',
        data: result.yearlyData.map(d => d.fireNumber),
        borderColor: '#ef4444',
        borderDash: [5, 5],
        tension: 0.4,
      },
    ]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="FIRE Calculator"
        description="Free FIRE Calculator India - Calculate Financial Independence Retire Early. Plan your FIRE number."
        keywords="FIRE calculator, financial independence calculator, retire early calculator, FIRE number calculator, early retirement planner"
        canonicalPath="/fire"
      />
      <div className="calculator-header">
        <h1>🔥 FIRE Calculator</h1>
        <p>Financial Independence, Retire Early - Plan your freedom!</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Current Age</label>
            <input
              type="number"
              value={currentAge === '' ? '' : currentAge}
              onChange={(e) => setCurrentAge(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Monthly Expenses (₹)</label>
              <input
                type="number"
                value={monthlyExpense === '' ? '' : monthlyExpense}
                onChange={(e) => setMonthlyExpense(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Monthly Savings (₹)</label>
              <input
                type="number"
                value={monthlySavings === '' ? '' : monthlySavings}
                onChange={(e) => setMonthlySavings(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Current Savings/Investments (₹)</label>
            <input
              type="number"
              value={currentSavings === '' ? '' : currentSavings}
              onChange={(e) => setCurrentSavings(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Pre-Retirement Return (%)</label>
              <input
                type="number"
                step="0.5"
                value={preRetireReturn === '' ? '' : preRetireReturn}
                onChange={(e) => setPreRetireReturn(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Post-Retirement Return (%)</label>
              <input
                type="number"
                step="0.5"
                value={postRetireReturn === '' ? '' : postRetireReturn}
                onChange={(e) => setPostRetireReturn(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Inflation Rate (%)</label>
              <input
                type="number"
                step="0.5"
                value={inflation === '' ? '' : inflation}
                onChange={(e) => setInflation(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Safe Withdrawal Rate (%)</label>
              <input
                type="number"
                step="0.5"
                value={withdrawalRate === '' ? '' : withdrawalRate}
                onChange={(e) => setWithdrawalRate(e.target.value === '' ? '' : Number(e.target.value))}
              />
              <small style={{ color: '#64748b', fontSize: '11px' }}>Standard is 4% (25x rule)</small>
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate FIRE Plan
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}>
                <h3>You Can Retire At Age</h3>
                <div className="amount">{result.fireAge}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  {result.yearsToFIRE} years from now | Savings Rate: {result.savingsRate.toFixed(0)}%
                </p>
              </div>

              <div className="result-details">
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">FIRE Number (Today)</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.fireNumber)}</span>
                </div>
                <div className="result-item">
                  <span className="label">FIRE Number at Retirement</span>
                  <span className="value">{formatCurrency(result.fireNumberAtRetirement)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Corpus at FIRE</span>
                  <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.corpusAtFIRE)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Monthly Income at FIRE</span>
                  <span className="value">{formatCurrency(result.monthlyIncomeAtFIRE)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Investment</span>
                  <span className="value">{formatCurrency(result.totalInvestment)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Wealth Gained</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.wealthGained)}</span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Path to FIRE</h4>
                  <Line data={chartData} options={{ 
                    responsive: true, 
                    plugins: { legend: { position: 'bottom' } },
                    scales: {
                      y: {
                        ticks: {
                          callback: (value) => '₹' + (value / 10000000).toFixed(1) + 'Cr'
                        }
                      }
                    }
                  }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download FIRE Plan PDF
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your details to calculate your FIRE timeline</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>🔥 FIRE Basics</h4>
        <ul>
          <li><strong>25x Rule:</strong> You need 25 times your annual expenses to retire (4% withdrawal rate)</li>
          <li><strong>Savings Rate is Key:</strong> Higher savings rate = Earlier retirement</li>
          <li>50% savings rate → ~17 years to FIRE</li>
          <li>70% savings rate → ~8 years to FIRE</li>
          <li>Consider healthcare costs and emergency fund separately</li>
        </ul>
      </div>
    </div>
  );
};

export default FIRECalculator;
